import { useTranslation } from "react-i18next";

import Card from "../Card/Card";
import classNames from "../../../utils/classNames";

export type WorkflowStepState = "done" | "in_progress" | "not_started";

export interface WorkflowStep {
  label: string;
  state: WorkflowStepState;
  lineActive?: boolean;
  onClick?: () => void;
  /** When truthy, the step is interactive. Omit for display-only steps. */
  isClickable?: boolean;
  /** Shown in the footer below the stepper (see `instructionDisplay`). */
  instructions?: string;
}

function getStepCircleClasses(state: WorkflowStepState) {
  if (state === "done") return "bg-green-600 text-white";
  if (state === "in_progress") return "bg-primary-600 text-white";
  return "bg-gray-200 text-gray-500";
}

function getStepLabelClasses(state: WorkflowStepState) {
  if (state === "done") return "text-green-700";
  if (state === "in_progress") return "text-primary-700";
  return "text-gray-500";
}

/** Which step’s `instructions` text to show in the footer. */
export type WorkflowInstructionDisplay = "active" | "all";

/** First in-progress step, else first not-started, else nothing (all complete). */
function getActiveStepForInstructions(steps: WorkflowStep[]) {
  return (
    steps.find((s) => s.state === "in_progress") ??
    steps.find((s) => s.state === "not_started") ??
    null
  );
}

export interface WorkflowStepperProps {
  title: string;
  steps: WorkflowStep[];
  /**
   * `active` — show instructions for the current workflow step (first `in_progress`, else first `not_started`).
   * `all` — list every step that has `instructions` set.
   */
  instructionDisplay?: WorkflowInstructionDisplay;
  bodyClassName?: string;
  /** Minimum width of the horizontal track (enables horizontal scroll on small screens). Default 980px. */
  minWidth?: number | string;
}

function formatMinWidth(minWidth: number | string | undefined) {
  if (minWidth === undefined) return "980px";
  if (typeof minWidth === "number") return `${minWidth}px`;
  return minWidth;
}

export default function WorkflowStepper({
  title,
  steps,
  instructionDisplay = "active",
  bodyClassName = "p-4",
  minWidth,
}: WorkflowStepperProps) {
  const { t } = useTranslation("common");
  const minW = formatMinWidth(minWidth);

  const stepStateLabel: Record<WorkflowStepState, string> = {
    done: t("Complete"),
    in_progress: t("Current"),
    not_started: t("Not Started"),
  };

  const activeStep = getActiveStepForInstructions(steps);
  const activeInstruction =
    instructionDisplay === "active" && activeStep?.instructions ? activeStep.instructions : "";

  const stepsWithInstructions =
    instructionDisplay === "all" ? steps.filter((s) => !!s.instructions?.trim()) : [];

  const showFooter =
    (instructionDisplay === "active" && !!activeInstruction) ||
    (instructionDisplay === "all" && stepsWithInstructions.length > 0);

  return (
    <Card title={title} bodyClassName={bodyClassName}>
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <div style={{ minWidth: minW }} className="min-w-0">
            <ol className="flex items-start">
              {steps.map((step, index) => (
                <li
                  key={`${index}-${step.label}`}
                  className="relative flex min-w-[130px] flex-1 flex-col items-center text-center"
                >
                  <button
                    type="button"
                    onClick={step.onClick}
                    disabled={!step.isClickable}
                    className={classNames(
                      "group relative z-10 flex flex-col items-center rounded-md px-2 py-1 transition-colors",
                      step.isClickable ? "cursor-pointer hover:bg-gray-50" : "cursor-default"
                    )}
                  >
                    <div
                      className={classNames(
                        "mb-2 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold",
                        getStepCircleClasses(step.state)
                      )}
                    >
                      {index + 1}
                    </div>
                    <p
                      className={classNames(
                        "text-[11px] font-semibold leading-4",
                        getStepLabelClasses(step.state)
                      )}
                    >
                      {step.label}
                    </p>
                    <p
                      className={classNames(
                        "mt-1 text-[10px] leading-4",
                        getStepLabelClasses(step.state)
                      )}
                    >
                      {stepStateLabel[step.state]}
                    </p>
                  </button>

                  {index < steps.length - 1 && (
                    <div className="absolute left-1/2 top-4 h-px w-full pl-4 pr-4">
                      <div
                        className={classNames(
                          "h-px w-full",
                          step.lineActive ? "bg-primary-500" : "bg-gray-300"
                        )}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ol>

            {showFooter && (
              <div className="space-y-2 pt-2 text-left">
                <hr />
                {instructionDisplay === "active" && (
                  <p className="text-xs text-gray-500 whitespace-pre-line">{activeInstruction}</p>
                )}
                {instructionDisplay === "all" && (
                  <ul className="list-inside list-disc space-y-1 text-xs text-gray-500">
                    {stepsWithInstructions.map((s, i) => (
                      <li key={`${i}-${s.label}`}>
                        <span className="font-medium text-gray-700">{s.label}: </span>
                        <span className="whitespace-pre-line">{s.instructions}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
